<template>
  <div class="mb-5">
    <div v-if="no_methods">
      {{ $t('payment.widget.modal.no_methods') }}
    </div>

    <z-form v-if="!no_methods" ref="form" @submit.prevent.native="submit">
      <input-payment-method v-model="form.payment_method" :list="list" />

      <div class="payment-block">
        <div
          v-if="form.payment_method && form.payment_method !== 'invoice_request'"
          :class="get_methods_class"
        >
          <div class="mb-1">{{ $t('payment.widget.modal.form.field.amount') }}</div>
          <div class="row">
            <z-form-input
              class="col-3"
              :rules="{ min_value: method_minimal, required: true }"
              :show_errors="false"
              vid="amount"
            >
              <i-input
                v-model="form.amount"
                :placeholder="$user.currency"
                type="number"
                class="payment-input"
              />
            </z-form-input>
          </div>
          <div class="row col-8 mb-0 payment-message">
            <b>
              {{ is_loading ? '' : message }}
              <br />
              {{ $t(`payment.widget.modal.form.method_message.${form.payment_method}`) }}
            </b>
          </div>
          <i-button
            v-if="form.payment_method && form.payment_method !== 'invoice_request'"
            class="mt-4 w-25"
            html-type="submit"
            type="success"
            size="large"
            :loading="is_button_loading"
            :disabled="!isPaymentAllowed"
          >
            {{ $t('payment.widget.modal.form.submit') }}
          </i-button>
        </div>

        <div v-if="form.payment_method === 'invoice_request'" :class="get_invoice_class">
          <div class="row mb-4">
            <div class="col-3">
              <div>
                {{ $t('payment.widget.modal.form.field.amount') }}
              </div>
              <z-form-input :rules="{ required: true }">
                <input-number
                  v-model="requestForm.amount"
                  :max="999999999.99"
                  :min="0.01"
                  :formatter="(num) => Math.floor(num * 100) / 100"
                  placeholder="0.00"
                  class="payment-input"
                />
              </z-form-input>
            </div>

            <div class="col-2">
              <div>{{ $t('core.register.field.currency') }}</div>

              <div>
                <i-select v-model="requestForm.currency">
                  <i-option v-for="item in currencies" :key="item" :label="item" :value="item" />
                </i-select>
                <tooltip
                  class="payment-tooltip"
                  placement="top"
                  min-width="400"
                  max-width="400"
                  theme="light"
                  :content="$t('payment.tooltip')"
                >
                  <icon size="18" type="ios-help-circle-outline" />
                </tooltip>
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-5">
              <div>
                {{ $t('payment.company_name') }}
              </div>

              <i-input
                ref="input"
                v-model="requestForm.company_name"
                type="textarea"
                class="payment-textarea no-resize"
                :maxlength="company_name_max_length"
                :rows="4"
                v-on="$listeners"
              />
              <div class="payment-comment-counter">
                {{ requestForm.company_name.length }}/{{ company_name_max_length }}
              </div>
              <tooltip
                class="payment-textarea__tooltip"
                placement="top"
                min-width="200"
                max-width="200"
                theme="light"
                :transfer="true"
                :content="$t('payment.company_name_tooltip')"
              >
                <icon size="18" type="ios-help-circle-outline" />
              </tooltip>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-5">
              <div>Emails</div>
              <div>
                <i-select
                  v-model="requestForm.email"
                  :multiple="true"
                  :filterable="true"
                  :allow-create="true"
                  :placeholder="$t('shared.component.select.select_or_type')"
                  @on-change="handle_change_email"
                  @on-create="handle_create_email"
                >
                  <i-option-group :label="$t('payment.general')">
                    <i-option v-for="item in emailsGroup" :key="item" :value="item">
                      {{ item }}
                    </i-option>
                  </i-option-group>
                  <i-option-group v-if="!!$user.finance_email" :label="$t('payment.finance_email')">
                    <i-option v-for="item in $user.finance_email" :key="item" :value="item">
                      {{ item }}
                    </i-option>
                  </i-option-group>
                  <i-option-group v-if="!!$user.email_rates" :label="$t('payment.email_rates')">
                    <i-option v-for="item in $user.email_rates" :key="item" :value="item">
                      {{ item }}
                    </i-option>
                  </i-option-group>
                  <i-option-group v-if="!!emailsOptional" :label="$t('payment.email_optional')">
                    <i-option v-for="item in emailsOptional" :key="item" :value="item">
                      {{ item }}
                    </i-option>
                  </i-option-group>
                </i-select>
                <div v-if="errors.email" class="form-input__warning form-input__error--absolute">
                  {{ $t('core.payment.errors.email') }}
                </div>

                <tooltip
                  class="payment-tooltip"
                  placement="top"
                  min-width="400"
                  max-width="400"
                  theme="light"
                  :content="$t('payment.tooltip_email')"
                >
                  <icon size="18" type="ios-help-circle-outline" />
                </tooltip>
              </div>
            </div>
          </div>
          <i-button
            v-if="form.payment_method === 'invoice_request' && !no_methods"
            class="mt-4 w-25"
            html-type="submit"
            type="success"
            size="large"
            :loading="is_invoice_loading"
          >
            {{ $t('payment.button') }}
          </i-button>
        </div>
      </div>
    </z-form>
  </div>
</template>

<script>
  import InputPaymentMethod from '@payment/components/input-payment-method';
  import { PaymentService } from '@payment/services/PaymentService';
  import { mapGetters } from 'vuex';
  import logo_yoomoney from '@public/img/payment/logo_yoomoney.svg';
  import logo_wm from '@public/img/payment/logo_wm.svg';
  import logo_capitalist from '@public/img/payment/logo_capitalist.png';
  import logo_paypal from '@public/img/payment/logo_paypal.png';
  import logo_card from '@public/img/payment/logo_card.png';
  import logo_invoice_request_RU from '@public/img/payment/invoice_request_ru.svg';
  import logo_invoice_request_EN from '@public/img/payment/invoice_request_en.svg';
  import logo_invoice_request_ID from '@public/img/payment/invoice_request_id.svg';
  import debounce from 'lodash-es/debounce';

  const round = (num, dec) => Math.ceil(num * Math.pow(10, dec)) / Math.pow(10, dec);

  // eslint-disable-next-line no-useless-escape
  const emailRegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  export default {
    components: {
      InputPaymentMethod
    },

    data: () => ({
      currencies: ['RUB', 'USD', 'EUR', 'IDR', 'KZT'],
      errors: {
        email: false
      },
      list: [],
      message: '',
      method_minimal: 1,
      is_loading: false,
      no_methods: false,
      is_invoice_loading: false,
      company_name_max_length: 200,
      validEmails: [],
      emailsGroup: [],
      emailsOptional: [],
      form: {
        amount: '',
        payment_method: ''
      },
      requestForm: {
        amount: '',
        currency: '',
        company_name: '',
        email: []
      },
      is_button_loading: false,
      is_invoice_request: false
    }),
    computed: {
      ...mapGetters('core/auth', ['user']),
      get_methods_class() {
        return this.is_button_loading ? 'payment-disabled' : undefined;
      },
      get_invoice_class() {
        return this.is_invoice_loading ? 'payment-disabled' : undefined;
      },

      isPaymentAllowed() {
        const method = this.form.payment_method;
        const amount = parseFloat(this.form.amount);
        const curr = this.$user.currency;

        if (curr === 'RUB' && method === 'capitalist') {
          return amount >= 20;
        }

        if (curr === 'RUB' && method === 'tinkoff') {
          return amount >= 160;
        }

        if (method) {
          return amount >= this.method_minimal;
        } else {
          return false;
        }
      }
    },
    watch: {
      '$i18n.locale': function (locale) {
        this.list = this.list.map((item) => {
          if (item.name === 'invoice_request') {
            let img = '';
            if (locale === 'ru') {
              img = logo_invoice_request_RU;
            } else if (locale === 'id') {
              img = logo_invoice_request_ID;
            } else {
              img = logo_invoice_request_EN;
            }
            return {
              ...item,
              img_src: img
            };
          } else {
            return item;
          }
        });
      },
      'form.amount': {
        handler: function () {
          const curr = this.$user.currency;
          const text = this.$t('payment.widget.modal.form.minimal_amount');
          const to = curr;

          const params = {
            amount: 1,
            from: 'USD'
          };

          if (curr === 'RUB') {
            this.message = `${text} 1 ₽`;

            if (this.form.payment_method === 'capitalist') {
              this.message = `${text} 20 ₽`;
            } else if (this.form.payment_method === 'tinkoff') {
              this.message = `${text} 160 ₽`;
            }
          } else if (curr === 'EUR') {
            this.message = `${text} 1 €`;
          } else if (curr === 'IDR') {
            this.is_loading = true;
            PaymentService.getConversions(params)
              .then((repsonse) => {
                this.method_minimal = round(repsonse[to].amount, 2);
                this.message = `${text}${this.method_minimal} Rp`;
                this.is_loading = false;
              })
              .catch((error) => this.$noty.server_error(error));
          } else if (curr === 'VND') {
            this.is_loading = true;
            PaymentService.getConversions(params)
              .then((repsonse) => {
                this.method_minimal = round(repsonse[to].amount, 2);
                this.message = `${text}${this.method_minimal} ₫`;
                this.is_loading = false;
              })
              .catch((error) => this.$noty.server_error(error));
          } else {
            this.message = `${text} 1 $`;
          }
        },
        immediate: true,
        deep: true
      },
      'form.payment_method': {
        handler: function () {
          const curr = this.$user.currency;
          const text = this.$t('payment.widget.modal.form.minimal_amount');
          const to = curr;

          const params = {
            amount: 1,
            from: 'USD'
          };

          if (curr === 'RUB') {
            this.method_minimal = 1;
            this.message = `${text} 1 ₽`;

            if (this.form.payment_method === 'capitalist') {
              this.method_minimal = 20;
              this.message = `${text} 20 ₽`;
            } else if (this.form.payment_method === 'tinkoff') {
              this.method_minimal = 160;
              this.message = `${text} 160 ₽`;
            }
          } else if (curr === 'EUR') {
            this.method_minimal = 1;
            this.message = `${text} 1 €`;
          } else if (curr === 'IDR') {
            this.is_loading = true;
            PaymentService.getConversions(params)
              .then((repsonse) => {
                this.method_minimal = round(repsonse[to].amount, 2);
                this.message = `${text}${this.method_minimal} Rp`;
                this.is_loading = false;
              })
              .catch((error) => this.$noty.server_error(error));
          } else if (curr === 'VND') {
            this.is_loading = true;
            PaymentService.getConversions(params)
              .then((repsonse) => {
                this.method_minimal = round(repsonse[to].amount, 2);
                this.message = `${text}${this.method_minimal} ₫`;
                this.is_loading = false;
              })
              .catch((error) => this.$noty.server_error(error));
          } else {
            this.method_minimal = 1;
            this.message = `${text} 1 $`;
          }
        },
        immediate: true,
        deep: true
      }
    },
    created() {
      const { currency, email, locale } = this.$user;
      const { available_payment_methods } = this.$config;
      this.requestForm.currency = this.currencies.find((i) => i === currency) || '';
      this.emailsGroup = [email];

      const all_methods = [
        'yoomoney',
        'webmoney',
        'capitalist',
        'tinkoff',
        'paypal',
        'invoice_request'
      ];

      this.list = all_methods
        .filter((x) => available_payment_methods.includes(x))
        .map((i) => {
          let image;

          /* eslint-disable no-unreachable, vue/script-indent */
          switch (i) {
            case 'yoomoney': {
              image = logo_yoomoney;

              break;
            }
            case 'webmoney': {
              image = logo_wm;

              break;
            }
            case 'capitalist': {
              image = logo_capitalist;

              break;
            }
            case 'paypal': {
              image = logo_paypal;

              break;
            }
            case 'tinkoff': {
              image = logo_card;

              break;
            }
            case 'invoice_request': {
              if (locale === 'ru') {
                image = logo_invoice_request_RU;
              } else if (locale === 'id') {
                image = logo_invoice_request_ID;
              } else {
                image = logo_invoice_request_EN;
              }

              break;
            }

            default: {
              image = '';

              break;
            }
          }

          return {
            type: i,
            img_src: image,
            name: i,
            disabled: false
          };
        });

      if (this.list?.length > 0) {
        this.form.payment_method = this.list[0].name;
      }

      if (this.list.length === 0) {
        this.no_methods = true;
      }
    },
    methods: {
      checkSearchStr: debounce(function () {
        const amount = parseFloat(this.form.amount);
        if (amount < this.method_minimal) {
          this.$noty.error(this.$t('payment.widget.modal.form.alert_minimum'));
        }
      }, 2000),

      handle_create_email(value) {
        if (emailRegExp.test(value)) {
          this.emailsOptional.push(value);
        }
      },

      handle_change_email(array) {
        if (this.requestForm.email.filter((i) => !emailRegExp.test(i)).length > 0) {
          this.errors.email = true;
        }
        const valid_emails = array.filter((i) => emailRegExp.test(i));
        this.requestForm.email = valid_emails;
      },

      submit(e) {
        e.preventDefault();
        e.stopPropagation();

        if (
          this.form.payment_method &&
          this.form.payment_method !== 'invoice_request' &&
          !this.is_button_loading
        ) {
          return this.save(e);
        }

        if (
          this.form.payment_method === 'invoice_request' &&
          !this.no_methods &&
          !this.is_invoice_loading
        ) {
          return this.request(e);
        }
      },

      save(e) {
        e.preventDefault();
        this.$refs.form.validate().then((is_valid) => {
          if (is_valid && !!this.form.payment_method) {
            this.is_button_loading = true;
            PaymentService.createItem(this.form)
              .then((res) => {
                if ('payUrl' in res) {
                  window.location = res.payUrl;
                }
              })
              .catch((event) => {
                // tinkoff custom backend rule
                const paymentMethodErrors = this.$R.pathOr(
                  '',
                  ['response', 'data', 'errors', 'payment_method'],
                  event
                );

                if (paymentMethodErrors.length > 0) {
                  paymentMethodErrors.map((i) => {
                    const regExp = /check_user_phone/g;
                    if (i.rule.match(regExp)) {
                      return this.$noty.show(this.$t('payment.errors.check_user_phone'));
                    } else {
                      return this.$noty.server_error(event);
                    }
                  });
                } else {
                  this.$noty.server_error(event);
                }
              })
              .finally(() => (this.is_button_loading = false));
          }
        });
      },

      request(e) {
        e.preventDefault();

        this.$refs.form.validate().then((is_valid) => {
          if (is_valid) {
            this.errors.email = false;
            this.is_invoice_loading = true;
            PaymentService.createRequest({
              ...this.requestForm,
              amount: Math.floor(this.requestForm.amount * 100) / 100
            })
              .then((res) => {
                if (res?.success === true) {
                  this.$noty.success(this.$t('payment.success'));
                } else {
                  this.$noty.error(this.$t('payment.asana_api_problems'));
                }
              })
              .catch((e) => this.check_server_validation(e))
              .finally(() => (this.is_invoice_loading = false));
          }
        });
      }
    }
  };
</script>

<style lang="scss">
  .payment-block {
    min-height: 157px;
  }
  .payment-tooltip {
    position: absolute;
    top: 26px;
    right: -10px;

    .ivu-tooltip-inner-with-width {
      min-width: 280px;
    }
  }

  .payment-textarea__tooltip {
    position: absolute;
    bottom: 0%;
    transform: translateX(calc(100% - 9px));
  }

  .payment-input.ivu-input-number {
    width: 100%;

    .ivu-input-number-handler-wrap {
      display: none;
    }
  }

  .payment-disabled {
    opacity: 0.7;
    pointer-events: none;
  }

  .payment-comment-counter {
    position: absolute;
    right: 20px;
    bottom: 2px;
    color: #bebebe;
    border-radius: 4px;
    background-color: #fff;
    opacity: 0.9;
  }
</style>
